/**
 * Gesture mobile calendario:
 * - corpo card: tap via onClick nativo (niente FSM touch → evita touchcancel Android)
 * - maniglia grip: long-press (~280ms) poi drag; passare il dito / scrollare non sposta
 * Desktop resta su pointer events in SchedulerTask.
 *
 * Importante: se l'app va in background a metà drag, touchend/touchcancel
 * possono non arrivare → listener window + scroll-lock restano attivi e
 * congelano il calendario. Per questo resettiamo su visibility/pagehide.
 */
import { useCallback, useEffect, useRef } from 'react';
import { emitSchedulerPointerDrag } from './pointerDragBus';

/** Soglia move sulla maniglia dopo long-press, prima di emit drag. */
export const HANDLE_MOVE_SLOP_PX = 8;
/** Hold sulla maniglia prima di armare il drag.
 * Breve: su iOS oltre ~350ms parte la selezione testo. */
export const HANDLE_LONG_PRESS_MS = 120;
/** In pending (già preventDefault) tollera micro-movimenti del dito. */
export const HANDLE_PRESS_CANCEL_SLOP_PX = 28;
export const POINTER_DRAG_THRESHOLD_PX = 10;
export const OPEN_SUPPRESS_MS = 700;
export const CLICK_SHIELD_MS = 480;
/** @deprecated corpo card non usa più slop; tenuta per import esterni. */
export const TAP_SLOP_PX = 32;
/** @deprecated */
export const SCROLL_SLOP_PX = TAP_SLOP_PX;

export const SCHEDULER_RESET_GESTURES_EVENT = 'scheduler:reset-gestures';

/** Chiama da SchedulerContainer (o altrove) per sbloccare gesture bloccate. */
export const emitSchedulerResetGestures = () => {
  try {
    window.dispatchEvent(new CustomEvent(SCHEDULER_RESET_GESTURES_EVENT));
  } catch (_) {
    /* ignore */
  }
};

/** Dispose dell’ultimo shield attivo (unmount / re-arm). */
let disposeSchedulerClickShield = null;

export const disposeSchedulerClickShieldNow = () => {
  disposeSchedulerClickShield?.();
  disposeSchedulerClickShield = null;
};

export const armSchedulerClickShield = () => {
  disposeSchedulerClickShieldNow();
  const controller = new AbortController();
  const suppress = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const types = ['click', 'mouseup'];
  types.forEach((type) => {
    document.addEventListener(type, suppress, { capture: true, signal: controller.signal });
  });
  let dispose = null;
  const timer = window.setTimeout(() => {
    controller.abort();
    if (disposeSchedulerClickShield === dispose) disposeSchedulerClickShield = null;
  }, CLICK_SHIELD_MS);
  dispose = () => {
    window.clearTimeout(timer);
    controller.abort();
    if (disposeSchedulerClickShield === dispose) disposeSchedulerClickShield = null;
  };
  disposeSchedulerClickShield = dispose;
  return dispose;
};

const findTouch = (event, identifier) => {
  const list = event.changedTouches?.length ? event.changedTouches : event.touches;
  if (!list) return null;
  for (let i = 0; i < list.length; i += 1) {
    if (list[i].identifier === identifier) return list[i];
  }
  return list[0] || null;
};

const unlockScrollParents = (scrollLockRef) => {
  const locked = scrollLockRef.current;
  if (!locked) return;
  if (locked.onScroll) {
    locked.targets.forEach(({ el }) => {
      el.removeEventListener('scroll', locked.onScroll);
    });
  }
  locked.targets.forEach(({ el, top, left }) => {
    el.scrollTop = top;
    el.scrollLeft = left;
  });
  scrollLockRef.current = null;
};

const lockScrollParents = (scrollLockRef, node) => {
  unlockScrollParents(scrollLockRef);
  const targets = [];
  let el = node?.parentElement || null;
  while (el && el !== document.documentElement) {
    const style = window.getComputedStyle(el);
    const canScrollY =
      /(auto|scroll|overlay)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
    const canScrollX =
      /(auto|scroll|overlay)/.test(style.overflowX) && el.scrollWidth > el.clientWidth;
    if (canScrollY || canScrollX) {
      targets.push({ el, top: el.scrollTop, left: el.scrollLeft });
    }
    el = el.parentElement;
  }
  if (!targets.length) return;

  const onScroll = () => {
    targets.forEach(({ el: target, top, left }) => {
      if (target.scrollTop !== top) target.scrollTop = top;
      if (target.scrollLeft !== left) target.scrollLeft = left;
    });
  };
  targets.forEach(({ el: target }) => {
    target.addEventListener('scroll', onScroll, { passive: true });
  });
  scrollLockRef.current = { targets, onScroll };
};

/**
 * Solo drag dalla maniglia. Il tap sul corpo è gestito da onClick in SchedulerTask.
 * @returns {{ handleRef }}
 */
export function useMobileTaskGestures({
  enabled,
  selectedTask,
  day,
  workerId,
  setStartData,
  setDragArmed,
  setDragPending,
  suppressOpenUntilRef,
}) {
  const fsmRef = useRef({ state: 'idle' });
  const scrollLockRef = useRef(null);
  const windowListenersRef = useRef(null);
  const callbacksRef = useRef({});

  callbacksRef.current = {
    selectedTask,
    day,
    workerId,
    setStartData,
    setDragArmed,
    setDragPending,
  };

  const clearPressTimer = useCallback((session = fsmRef.current) => {
    if (session?.pressTimer) {
      window.clearTimeout(session.pressTimer);
      session.pressTimer = null;
    }
  }, []);

  const detachWindowListeners = useCallback(() => {
    if (!windowListenersRef.current) return;
    const { move, end, cancel } = windowListenersRef.current;
    window.removeEventListener('touchmove', move, true);
    window.removeEventListener('touchend', end, true);
    window.removeEventListener('touchcancel', cancel, true);
    windowListenersRef.current = null;
  }, []);

  const resetFsm = useCallback(
    ({ clearStartData = false } = {}) => {
      const session = fsmRef.current;
      clearPressTimer(session);
      if (session?.target) {
        session.target.style.touchAction = '';
      }
      detachWindowListeners();
      unlockScrollParents(scrollLockRef);
      fsmRef.current = { state: 'idle' };
      callbacksRef.current.setDragArmed?.(false);
      callbacksRef.current.setDragPending?.(false);
      if (clearStartData) callbacksRef.current.setStartData?.({});
    },
    [clearPressTimer, detachWindowListeners],
  );

  /** Abort completo: sblocca UI + chiude eventuale drag preview senza drop. */
  const abortOpenGestures = useCallback(() => {
    const session = fsmRef.current;
    const hasLock = Boolean(scrollLockRef.current);
    const hasListeners = Boolean(windowListenersRef.current);
    if ((!session || session.state === 'idle') && !hasLock && !hasListeners) return;

    if (session?.state === 'drag' && session.moved) {
      emitSchedulerPointerDrag({
        phase: 'end',
        type: 'dragged',
        clientX: -9999,
        clientY: -9999,
        sourceWorkerId: session.sourceWorkerId,
      });
    }
    resetFsm({ clearStartData: true });
    if (suppressOpenUntilRef) {
      suppressOpenUntilRef.current = Date.now() + OPEN_SUPPRESS_MS;
    }
  }, [resetFsm, suppressOpenUntilRef]);

  const suppressGhostOpen = useCallback(() => {
    suppressOpenUntilRef.current = Date.now() + OPEN_SUPPRESS_MS;
    armSchedulerClickShield();
  }, [suppressOpenUntilRef]);

  const commitDragPreview = useCallback((session) => {
    if (!session || session.startDataSet) return;
    session.startDataSet = true;
    callbacksRef.current.setStartData({
      type: 'dragged',
      workerId: session.workerId,
      task: session.task,
      day: session.day,
    });
  }, []);

  const attachWindowListeners = useCallback(
    (session) => {
      detachWindowListeners();

      const move = (event) => {
        const current = fsmRef.current;
        if (!current || current.id !== session.id) return;
        if (current.state !== 'pending' && current.state !== 'drag') return;

        const touch = findTouch(event, current.id);
        if (!touch) return;

        const distance = Math.hypot(touch.clientX - current.startX, touch.clientY - current.startY);
        current.maxDistance = Math.max(current.maxDistance || 0, distance);
        current.lastX = touch.clientX;
        current.lastY = touch.clientY;

        // Hold non ancora armato: blocca selezione/scroll iOS; se il dito scappa troppo → annulla.
        if (current.state === 'pending') {
          if (event.cancelable) event.preventDefault();
          event.stopPropagation();
          if (distance >= HANDLE_PRESS_CANCEL_SLOP_PX) {
            resetFsm({ clearStartData: true });
          }
          return;
        }

        if (event.cancelable) event.preventDefault();
        event.stopPropagation();

        if (!current.moved && distance < HANDLE_MOVE_SLOP_PX) return;
        if (!current.moved) {
          current.moved = true;
          commitDragPreview(current);
          suppressGhostOpen();
        }
        emitSchedulerPointerDrag({
          phase: 'move',
          type: 'dragged',
          clientX: touch.clientX,
          clientY: touch.clientY,
          sourceWorkerId: current.sourceWorkerId,
        });
      };

      const finish = (event, { fromCancel = false } = {}) => {
        const current = fsmRef.current;
        if (!current || current.id !== session.id) return;
        if (current.state !== 'pending' && current.state !== 'drag') return;

        // Rilascio prima del long-press: nessun drag.
        if (current.state === 'pending') {
          resetFsm({ clearStartData: true });
          return;
        }

        const touch = findTouch(event, current.id);
        const clientX = touch?.clientX ?? current.lastX ?? current.startX;
        const clientY = touch?.clientY ?? current.lastY ?? current.startY;
        const wasMoved = current.moved;
        const sourceWorkerId = current.sourceWorkerId;

        if (fromCancel || !wasMoved) {
          if (wasMoved) {
            emitSchedulerPointerDrag({
              phase: 'end',
              type: 'dragged',
              clientX: -9999,
              clientY: -9999,
              sourceWorkerId,
            });
          }
          resetFsm({ clearStartData: true });
          suppressGhostOpen();
          return;
        }

        suppressGhostOpen();
        emitSchedulerPointerDrag({
          phase: 'end',
          type: 'dragged',
          clientX,
          clientY,
          sourceWorkerId,
        });
        resetFsm({ clearStartData: false });
      };

      const end = (event) => finish(event, { fromCancel: false });
      const cancel = (event) => finish(event, { fromCancel: true });

      // pending: passive move così lo scroll del calendario resta fluido.
      // drag: passive:false per preventDefault e bloccare lo scroll durante lo spostamento.
      window.addEventListener('touchmove', move, { capture: true, passive: false });
      window.addEventListener('touchend', end, { capture: true, passive: true });
      window.addEventListener('touchcancel', cancel, { capture: true, passive: true });
      windowListenersRef.current = { move, end, cancel };
    },
    [commitDragPreview, detachWindowListeners, resetFsm, suppressGhostOpen],
  );

  const armHandleDrag = useCallback(
    (sessionId) => {
      const session = fsmRef.current;
      if (!session || session.state !== 'pending' || session.id !== sessionId) return;

      clearPressTimer(session);
      session.state = 'drag';
      session.pressTimer = null;
      callbacksRef.current.setDragPending?.(false);
      callbacksRef.current.setDragArmed(true);
      if (session.target) {
        session.target.style.touchAction = 'none';
      }
      lockScrollParents(scrollLockRef, session.target);
      // iOS non espone Vibration API: il feedback principale è visivo (grip_armed).
      try {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
          navigator.vibrate([0, 20, 35, 20]);
        }
      } catch (_) {
        /* ignore */
      }
    },
    [clearPressTimer],
  );

  const startHandleDrag = useCallback(
    (event) => {
      if (!enabled) return;
      if (event.touches.length !== 1) return;

      if (fsmRef.current.state !== 'idle') {
        resetFsm({ clearStartData: true });
      }

      const touch = event.touches[0];
      // Su iPhone senza preventDefault il long-press seleziona il testo della pagina
      // (stesso comportamento del drag originale, che partiva subito al tocco).
      event.stopPropagation();
      if (event.cancelable) event.preventDefault();

      const session = {
        state: 'pending',
        id: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        lastX: touch.clientX,
        lastY: touch.clientY,
        startTime: Date.now(),
        maxDistance: 0,
        moved: false,
        startDataSet: false,
        pressTimer: null,
        target: event.currentTarget,
        task: callbacksRef.current.selectedTask,
        day: callbacksRef.current.day,
        workerId: callbacksRef.current.workerId,
        sourceWorkerId: callbacksRef.current.workerId,
      };

      session.pressTimer = window.setTimeout(() => {
        armHandleDrag(session.id);
      }, HANDLE_LONG_PRESS_MS);

      callbacksRef.current.setDragPending?.(true);
      fsmRef.current = session;
      attachWindowListeners(session);
    },
    [armHandleDrag, attachWindowListeners, enabled, resetFsm],
  );

  const handleNodeRef = useRef(null);
  const handleListenerRef = useRef(null);
  const handleStartRef = useRef(null);
  handleStartRef.current = startHandleDrag;

  const handleRef = useCallback(
    (node) => {
      const prev = handleNodeRef.current;
      if (prev && handleListenerRef.current) {
        prev.removeEventListener('touchstart', handleListenerRef.current);
      }
      handleNodeRef.current = node;
      handleListenerRef.current = null;
      if (!node || !enabled) return;
      const onStart = (event) => handleStartRef.current?.(event);
      handleListenerRef.current = onStart;
      node.addEventListener('touchstart', onStart, { passive: false });
    },
    [enabled],
  );

  useEffect(() => {
    return () => {
      detachWindowListeners();
      unlockScrollParents(scrollLockRef);
      fsmRef.current = { state: 'idle' };
      const node = handleNodeRef.current;
      if (node && handleListenerRef.current) {
        node.removeEventListener('touchstart', handleListenerRef.current);
      }
      handleNodeRef.current = null;
      handleListenerRef.current = null;
    };
  }, [detachWindowListeners]);

  useEffect(() => {
    if (!enabled && fsmRef.current.state !== 'idle') {
      resetFsm({ clearStartData: true });
    }
  }, [enabled, resetFsm]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onVisibilityChange = () => {
      abortOpenGestures();
    };
    const onPageHide = () => abortOpenGestures();
    const onPageShow = () => abortOpenGestures();
    const onBusReset = () => {
      disposeSchedulerClickShieldNow();
      abortOpenGestures();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener(SCHEDULER_RESET_GESTURES_EVENT, onBusReset);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener(SCHEDULER_RESET_GESTURES_EVENT, onBusReset);
    };
  }, [abortOpenGestures, enabled]);

  return {
    handleRef,
  };
}
